import type { CompleteVariant, RequiredProduct } from '$lib/types';
import { isProd, type MetaField, sleep, withTimeout } from '$lib/utils';
import {
	type ISbResult,
	type ISbStories,
	type ISbStoriesParams,
	type ISbStory,
	type ISbStoryData,
	useStoryblokApi
} from '@storyblok/svelte';
import { dev } from '$app/environment';

export type ProductStoryData = {
	product: ISbStoryData[];
	variant: ISbStoryData[];
};

export type CompleteStoryData = {
	care: ProductStoryData;
	material: ProductStoryData;
	faq: ISbStoryData[];
	measure: ISbStoryData[];
} & ProductStoryData;

export const fetchStoryblokContentFor = (
	product: RequiredProduct,
	variant: CompleteVariant,
	locale: string
) => {
	function parseSlugs(metafield: MetaField): string[] {
		const fieldValue = metafield?.value;
		if (!fieldValue) {
			return [];
		}
		const parsed = fieldValue.startsWith('[') ? JSON.parse(fieldValue ?? '[]') : [fieldValue];
		return (parsed as string[]).map((slug) => slug.trim().replace(/^\/+/, ''));
	}

	async function _resolveSingleStory(
		metafieldKey: string,
		path: string,
		lang: string,
		filter: (slug: string) => boolean = () => true
	): Promise<ProductStoryData> {
		if (!product) {
			throw new Error('Tried to get stories from non existing product');
		}
		const productSlugs = parseSlugs(product[metafieldKey]).filter(filter);
		const variantSlugs = parseSlugs(variant[metafieldKey]).filter(filter);
		return Promise.all([
			resolveStories(productSlugs, path, lang),
			resolveStories(variantSlugs, path, lang)
		]).then(([productStory, variantStory]) => {
			return {
				product: productStory,
				variant: variantStory
			};
		});
	}

	return Promise.all([
		_resolveSingleStory(
			'content_d2c',
			'content',
			locale,
			(s) => s.startsWith('/product-blocks') || s.startsWith('product-blocks')
		),
		_resolveSingleStory(
			'content_d2c',
			'content/product-blocks',
			locale,
			(s) => !s.startsWith('/product-blocks') && !s.startsWith('product-blocks')
		),
		_resolveSingleStory('care_d2c', 'content/care', locale, (s) => s.split('/').length === 1),
		_resolveSingleStory('care_d2c', 'content', locale, (s) => s.split('/').length > 1),
		_resolveSingleStory(
			'material_d2c',
			'content/material',
			locale,
			(s) => s.split('/').length === 1
		),
		_resolveSingleStory('material_d2c', 'content', locale, (s) => s.split('/').length > 1),
		_resolveSingleStory('faq_d2c', 'content/question-answer', locale, () => true),
		_resolveSingleStory('measure_d2c', 'content/size', locale, () => true)
	]).then(
		([
			contentStories,
			productBlockContentStories,
			careContent,
			careContentBlocks,
			materialContent,
			materialContentBlock,
			faqContent,
			measureContent
		]) => {
			const contentStoriesJoined = {
				product: [...contentStories.product, ...productBlockContentStories.product],
				variant: [...contentStories.variant, ...productBlockContentStories.variant],
				care: {
					product: [...careContent.product, ...careContentBlocks.product],
					variant: [...careContent.variant, ...careContentBlocks.variant]
				},
				material: {
					product: [...materialContent.product, ...materialContentBlock.product],
					variant: [...materialContent.variant, ...materialContentBlock.variant]
				},
				faq: faqContent.variant?.length ? faqContent.variant : faqContent.product,
				measure: measureContent.variant?.length ? measureContent.variant : measureContent.product
			};
			return contentStoriesJoined;
		}
	);
};

export async function resolveStories(
	slugs: string[] | undefined,
	path: string,
	locale = 'de'
): Promise<ISbStoryData<{ [index: string]: any }>[]> {
	let stories: ISbStoryData<{ [index: string]: any }>[] = [];
	if (slugs?.length) {
		try {
			const storyPaths = slugs.map((slug) => `${path}/${slug}`.replace(/\/\//g, '/'));
			// puuh.. 🥵
			// Get all the stories you can get. Just don't crash.
			const storyblokResults = await Promise.allSettled(
				storyPaths.map((path) => getStory(path, locale))
			);
			stories = storyblokResults
				.filter((r) => r.status === 'fulfilled')
				.map((r) => (r as PromiseFulfilledResult<ISbStory>).value?.data?.story)
				.filter(Boolean);
			// if didn't get all stories we wanted to, simply log here 🗒
			if (storyPaths.length > stories.length) {
				const missing = storyPaths.filter(
					(_, idx) =>
						storyblokResults[idx].status !== 'fulfilled' ||
						!storyblokResults[idx].value?.data?.story
				);
				console.error(
					`Only ${stories.length} out of ${storyPaths.length} stories found, these are missing:\n  ${missing.join('\n  ')}`
				);
			}
		} catch (e) {
			console.error('Error while fetching storyblok content in [slug]/[handle].tsx');
		}
	}
	return stories;
}

export const getStories = async (
	language = 'de-de',
	more?: Partial<ISbStoriesParams>
): Promise<ISbStories> => {
	const preview = !isProd(); // TODO: part the variable STORYBLOK_PREVIEW_MODE === 'true';
	// the storyblok params
	const params: ISbStoriesParams = {
		language: language.split('-')[0],
		version: 'published',
		fallback_lang: 'de',
		...more
	};

	// checks if Next.js is in preview mode
	if (preview) {
		// loads the draft version
		params.version = 'draft';
		// appends the cache version to get the latest content
		if (!dev) {
			params.cv = Date.now();
		}
	}

	try {
		return useStoryblokApi().getStories(params);
	} catch (rawError) {
		try {
			const error = JSON.parse(typeof rawError === 'string' ? rawError : JSON.stringify(rawError));
			const timeoutCauses = ['ERR_SOCKET_CONNECTION_TIMEOUT', 'UND_ERR_CONNECT_TIMEOUT'];
			if (timeoutCauses.includes(error?.message?.cause?.code)) {
				console.warn(`Seems like a timeout error. Waiting 10 second and try again.`);
				await sleep(10010);
				return await getStories(language, params);
			}

			if (error.message === 'TypeError: fetch failed') {
				console.warn(`Seems like a timeout error. Waiting 10 second and try again.`);
				await sleep(10010);
				return await getStories(language, params);
			}

			if (error.status === 404) {
				return { data: null };
			}
			console.warn(
				`Error while fetching story from storyblok: slug: '', params: ${params.cv}, ${params.version}`
			);

			if (rawError?.status === 429) {
				console.warn(`Seems like a timeout error. Waiting 10 second and try again.`);
				await sleep(10010);
				return await getStories(language, params);
			} else {
				console.error(error);
			}
		} catch (e) {
			console.error(e);
		}

		return { data: null };
	}
};

/**
 * Get the story from storyblok.
 * @param slug of the story content
 * @returns data with story
 */
export const getStory = async (
	slug: string,
	language = 'de-de',
	more?: Partial<ISbStoriesParams>
): Promise<ISbResult | { data: null }> => {
	const preview = !isProd(); // TODO: part the variable STORYBLOK_PREVIEW_MODE === 'true';
	// the storyblok params
	const params: ISbStoriesParams = {
		language: language.split('-')[0],
		version: 'published',
		fallback_lang: 'de',
		...more
	};

	// checks if Next.js is in preview mode
	if (preview) {
		// loads the draft version
		params.version = 'draft';
		// appends the cache version to get the latest content
		if (!dev) {
			params.cv = Date.now();
		}
	}

	// return the story from Storyblok and whether preview mode is active
	try {
		const result = await withTimeout(useStoryblokApi().get(`cdn/stories/${slug}`, params), 10000);
		return result;
	} catch (rawError) {
		try {
			const error = JSON.parse(typeof rawError === 'string' ? rawError : JSON.stringify(rawError));
			const timeoutCauses = ['ERR_SOCKET_CONNECTION_TIMEOUT', 'UND_ERR_CONNECT_TIMEOUT'];
			if (timeoutCauses.includes(error?.message?.cause?.code)) {
				console.warn(`Seems like a timeout error. Waiting 10 second and try again.`);
				await sleep(10010);
				return await getStory(slug, language, params);
			}

			if (error.message === 'TypeError: fetch failed') {
				console.warn(`Seems like a timeout error. Waiting 10 second and try again.`);
				await sleep(10010);
				return await getStory(slug, language, params);
			}

			if (error.status === 404) {
				return { data: null };
			}
			console.warn(
				`Error while fetching story from storyblok: slug: ${slug}, params: ${params.cv}, ${params.version}`
			);

			if (rawError?.status === 429) {
				console.warn(`Seems like a timeout error. Waiting 10 second and try again.`);
				await sleep(10010);
				return await getStory(slug, language, params);
			} else {
				console.error(error);
			}
		} catch (e) {
			console.error(e);
		}

		return { data: null };
	}
};
